import { Module } from 'vuex';
import publicApi from '@/services/publicApi';
import { clearPublicCheckoutContext, publicEmailVerified, publicToken, savePublicToken, PublicCustomerAccount } from '@/support/publicSession';

interface PublicAuthState { token: string; customer: PublicCustomerAccount | null; loaded: boolean }
const publicAuth: Module<PublicAuthState, Record<string, unknown>> = {
  namespaced: true,
  state: (): PublicAuthState => ({ token: publicToken(), customer: null, loaded: false }),
  getters: {
    customer: (state) => state.customer,
    isAuthenticated: (state) => Boolean(state.token && state.customer?.is_active),
    isVerified: (state) => publicEmailVerified(state.customer),
  },
  mutations: {
    SESSION(state, { token, customer }) {
      if (token !== undefined) { savePublicToken(token); state.token = token; }
      state.customer = customer;
      state.loaded = true;
    },
    CLEAR(state) { savePublicToken(''); state.token = ''; state.customer = null; state.loaded = true; },
  },
  actions: {
    async restore({ state, commit }, options: { force?: boolean } = {}) {
      const token = publicToken();
      if (!token) { commit('CLEAR'); return null; }
      if (state.loaded && state.token === token && !options.force) return state.customer;
      try {
        const response = await publicApi.get('/public/auth/me');
        if (publicToken() !== token) return null;
        commit('SESSION', { token, customer: response.data.data.customer });
        return response.data.data.customer;
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 401 && publicToken() === token) commit('CLEAR');
        if (status !== 401) throw error;
        return null;
      }
    },
    async login({ commit }, credentials) {
      const response = await publicApi.post('/public/auth/login', credentials);
      commit('SESSION', response.data.data);
      return response.data.data.customer;
    },
    async register({ commit }, fields) {
      const response = await publicApi.post('/public/auth/register', fields);
      commit('SESSION', response.data.data);
      return response.data.data.customer;
    },
    async logout({ commit }) {
      try { await publicApi.post('/public/auth/logout'); }
      finally { commit('CLEAR'); clearPublicCheckoutContext(); }
    },
  },
};
export default publicAuth;
